import type { ITokenService, IStorageService } from "@/application/ports/services";
import { STORAGE_KEYS } from "./constants";
import { logger } from "@/infrastructure/logger";

export class TokenService implements ITokenService {
  private storageService: IStorageService;

  constructor(storageService: IStorageService) {
    this.storageService = storageService;
  }

  getToken(): string | null {
    return this.storageService.get<string>(STORAGE_KEYS.AUTH_TOKEN);
  }

  setToken(token: string): void {
    this.storageService.set(STORAGE_KEYS.AUTH_TOKEN, token);
  }

  removeToken(): void {
    this.storageService.remove(STORAGE_KEYS.AUTH_TOKEN);
  }

  isTokenExpired(token: string): boolean {
    try {
      // For real JWT tokens, decode and check expiry
      const payloadSegment = token.split(".")[1];
      if (token && token.split(".").length === 3 && payloadSegment) {
        const payload = JSON.parse(atob(payloadSegment));
        const now = Date.now() / 1000;
        const EXPIRY_BUFFER_SECONDS = 30;
        return payload.exp < now + EXPIRY_BUFFER_SECONDS;
      }

      // If we can't parse the token, consider it expired
      return true;
    } catch (error) {
      logger.error("Error checking token expiry:", error);
      // If we can't parse the token, consider it expired
      return true;
    }
  }

  getTokenExpiryDate(token: string): Date | null {
    try {
      // For real JWT tokens, decode and get expiry date
      const payloadSegment = token.split(".")[1];
      if (token && token.split(".").length === 3 && payloadSegment) {
        const payload = JSON.parse(atob(payloadSegment));
        return new Date(payload.exp * 1000);
      }

      // If we can't parse the token, return null
      return null;
    } catch (error) {
      logger.error("Error getting token expiry date:", error);
      return null;
    }
  }

  getRefreshToken(): string | null {
    return this.storageService.get<string>(STORAGE_KEYS.AUTH_REFRESH_TOKEN);
  }

  setRefreshToken(refreshToken: string): void {
    this.storageService.set(STORAGE_KEYS.AUTH_REFRESH_TOKEN, refreshToken);
  }

  removeRefreshToken(): void {
    this.storageService.remove(STORAGE_KEYS.AUTH_REFRESH_TOKEN);
  }

  clearTokens() {
    this.removeToken();
    this.removeRefreshToken();
  }
}
